import { Plus } from "lucide-react";

import CuoButton from "@/app/admin/_common/CuoButton";
import { cn } from "@/app/_utils/cn";

import { checklistIcon } from "@/app/admin/_common/checklistIcons";

import type { DraftCategory, DraftField, DraftSection } from "../checklistDraft";
import { useDragReorder } from "../useDragReorder";
import FieldRow from "./FieldRow";
import { FIELD_GRID } from "./fieldGrid";

export default function SectionCard({
  section,
  category,
  onAddField,
  onUpdateField,
  onMoveField,
  onReorderField,
  onRemoveField,
  highlightNonceFor,
}: {
  section: DraftSection;
  category: DraftCategory;
  onAddField: (atStart?: boolean) => void;
  onUpdateField: (fieldUid: string, patch: Partial<DraftField>) => void;
  onMoveField: (fieldIndex: number, delta: number) => void;
  onReorderField: (from: number, to: number) => void;
  onRemoveField: (fieldUid: string) => void;
  highlightNonceFor: (uid: string) => number | null;
}) {
  const { dragIndex, handlers, dropPositionFor } = useDragReorder(onReorderField);
  const CategoryIcon = checklistIcon(category.icon);

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-end gap-3 px-1">
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className="flex items-center gap-1.5 text-[12px] text-muted-foreground">
            <CategoryIcon className="size-3.5" />
            {category.label || "Untitled category"}
          </span>
          <h2 className="my-0 truncate text-[20px] font-semibold tracking-tight text-foreground">
            {section.label || "Untitled section"}
          </h2>
        </div>
        {/* Adds to the top so the new field is visible without scrolling a long section. */}
        <CuoButton
          variant="outline"
          icon={<Plus className="size-4" />}
          label="Add field"
          onClick={() => onAddField(true)}
        />
      </div>

      <div className="flex flex-col gap-3">
        {/* No overflow-hidden here: it would make the card the sticky header's scroll container. */}
        <div className="flex flex-col rounded-md border border-border bg-card">
          <div
            className={cn(
              "sticky top-0 z-10 hidden rounded-t-md border-b border-border bg-muted py-2 pl-2 pr-4",
              FIELD_GRID
            )}
          >
            {/* pl-7 clears the grip (w-6 + gap-1) so "Field" lines up with the type icons. */}
            <span className="pl-7 text-[11.5px] font-medium text-muted-foreground">Field</span>
            <span className="text-[11.5px] font-medium text-muted-foreground">Type</span>
            <span className="text-[11.5px] font-medium text-muted-foreground text-center">Required</span>
            <span className="text-center text-[11.5px] font-medium text-muted-foreground">Order</span>
            <span />
          </div>

          {section.fields.length === 0 && (
            <p className="m-0 px-4 py-6 text-center text-[12.5px] text-muted-foreground">
              No fields yet
            </p>
          )}

          {section.fields.map((field, fieldIndex) => (
            <FieldRow
              key={field.uid}
              field={field}
              index={fieldIndex}
              count={section.fields.length}
              onChange={(patch) => onUpdateField(field.uid, patch)}
              onMove={(delta) => onMoveField(fieldIndex, delta)}
              onRemove={() => onRemoveField(field.uid)}
              dragHandlers={handlers(fieldIndex)}
              dropPosition={dropPositionFor(fieldIndex)}
              isDragging={dragIndex === fieldIndex}
            highlightNonce={highlightNonceFor(field.uid)}
            />
          ))}

        </div>

        <button
          type="button"
          onClick={() => onAddField()}
          className="flex w-full items-center justify-center gap-1.5 rounded-md border border-dashed border-border py-3 text-[13px] font-medium text-foreground hover:bg-muted/50"
        >
          <Plus className="size-4" />
          Add field
        </button>
      </div>
    </div>
  );
}
